// src/hooks/useLedger.ts
import { useCallback, useState } from "react";
import { getInvoiceByNumber } from "../firebase/invoice/getInvoiceByNumber";
import { getReturnsByDateRange } from "../firebase/returns/getReturnsByDateRange";
import { getPaymentsByDateRange } from "../firebase/payment/getPaymentsByDateRange";
import { getPaymentsByInvoiceNumber } from "../firebase/payment/getPaymentsByInvoiceNumber";

export type LedgerEntry = {
	date: string;
	type: "Payment" | "Return";
	details: string;
	amount: number;
	invoiceNumber: string;
	mode: "CASH" | "UPI" | null;
};

export const useLedger = () => {
	const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
	const [loading, setLoading] = useState(true);

	const fetchLedgerData = useCallback(async (start: string, end: string) => {
		setLoading(true);
		try {
			// Fetch all payments in date range
			const payments = (await getPaymentsByDateRange(start, end)) || [];
			const returns = (await getReturnsByDateRange(start, end)) || [];

			// Map payments to ledger entries
			const paymentEntries: LedgerEntry[] = payments.map((payment) => ({
				date: payment.paymentDate,
				type: "Payment",
				details: `Payment for Invoice ${payment.invoiceNumber}`,
				amount: payment.amount,
				invoiceNumber: payment.invoiceNumber,
				mode: payment.mode || null
			}));

			const cashRefundUsedByInvoice: Record<string, number> = {};
			const outstandingCompensatedByInvoice: Record<string, number> = {};

			const sortedReturns = [...returns].sort(
				(a, b) =>
					new Date(a.returnDate).getTime() -
					new Date(b.returnDate).getTime()
			);

			const returnEntries: LedgerEntry[] = [];
			for (const ret of sortedReturns) {
				const invoice = await getInvoiceByNumber(ret.invoiceNumber);
				const invoiceTotal = invoice
					? invoice.items.reduce(
							(total, item) =>
								total + item.sellPrice * item.quantity,
							0
						)
					: 0;

				const returnAmount = ret.amount * ret.quantity;
				const allPaymentsForInvoice = await getPaymentsByInvoiceNumber(
					ret.invoiceNumber
				);
				const paymentsForInvoice = allPaymentsForInvoice.filter(
					(p) =>
						new Date(p.paymentDate).getTime() <=
						new Date(ret.returnDate).getTime()
				);

				const totalPaidForInvoice = paymentsForInvoice.reduce(
					(sum, p) => sum + p.amount,
					0
				);

				const originalOutstanding = invoiceTotal - totalPaidForInvoice;
				const alreadyCompensatedOutstanding =
					outstandingCompensatedByInvoice[ret.invoiceNumber] || 0;
				const currentOutstanding =
					originalOutstanding - alreadyCompensatedOutstanding;

				const outstandingReduction = Math.min(
					Math.max(0, currentOutstanding),
					returnAmount
				);
				const cashRefundNeeded = Math.max(
					0,
					returnAmount - outstandingReduction
				);

				outstandingCompensatedByInvoice[ret.invoiceNumber] =
					alreadyCompensatedOutstanding + outstandingReduction;

				const alreadyRefundedCash =
					cashRefundUsedByInvoice[ret.invoiceNumber] || 0;
				const availableCashRefund = Math.max(
					0,
					totalPaidForInvoice - alreadyRefundedCash
				);
				const cashRefund = Math.min(
					cashRefundNeeded,
					availableCashRefund
				);

				cashRefundUsedByInvoice[ret.invoiceNumber] =
					alreadyRefundedCash + cashRefund;

				returnEntries.push({
					date: ret.returnDate,
					type: "Return",
					details: `Return from Invoice ${ret.invoiceNumber}`,
					amount: -cashRefund,
					invoiceNumber: ret.invoiceNumber,
					mode: "CASH"
				});
			}

			// Combine and sort by date descending
			const combinedEntries = [...paymentEntries, ...returnEntries];
			combinedEntries.sort(
				(a, b) =>
					new Date(b.date).getTime() - new Date(a.date).getTime()
			);

			setLedgerEntries(combinedEntries);
		} catch (err) {
			console.error("Failed to load ledger data:", err);
		} finally {
			setLoading(false);
		}
	}, []);

	return { ledgerEntries, loading, fetchLedgerData };
};
